import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

// LocalStorage key for a pending ?ref= captured before signup. The
// Frontpage writes this when a fresh visitor lands via a referral
// link; Auth.tsx reads it after successful signup and calls the
// redemption edge function.
const PENDING_REF_KEY = "pending_referral_code";

export function capturePendingReferral(code: string): void {
  try {
    localStorage.setItem(PENDING_REF_KEY, code.toUpperCase().trim());
  } catch {
    // Private browsing / SSR — fail silently. Referral is a nice-to-have.
  }
}

export function consumePendingReferral(): string | null {
  try {
    const code = localStorage.getItem(PENDING_REF_KEY);
    if (code) localStorage.removeItem(PENDING_REF_KEY);
    return code;
  } catch {
    return null;
  }
}

/**
 * Fetches the current user's referral code, minting one on first
 * call via the get_or_create_referral_code RPC. Returns the share
 * link too (origin + ?ref=CODE) so callers can copy it directly.
 */
export function useReferralCode() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["referral-code", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_or_create_referral_code");
      if (error) throw error;
      const code = String(data ?? "");
      // Deellink op het domein waar de gebruiker nu zit (fitnessappie.nl in
      // productie), zodat een uitnodiging nooit naar een oud adres wijst.
      const origin = typeof window !== "undefined" ? window.location.origin : "https://fitnessappie.nl";
      const url = `${origin}/?ref=${code}`;
      return { code, url };
    },
    staleTime: 60 * 60 * 1000, // 1h — the code never changes
  });
}

/**
 * Lists redemptions where the current user was the referrer.
 * Shown in the Profile invite section so users can see who they
 * brought in.
 */
export function useReferralStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["referral-stats", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referral_redemptions")
        .select("id, redeemed_at, reward_granted")
        .eq("referrer_id", user!.id)
        .order("redeemed_at", { ascending: false });
      if (error) throw error;
      const rows = data ?? [];
      return {
        count: rows.length,
        rewarded: rows.filter((r) => r.reward_granted).length,
        recent: rows,
      };
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Redeems any pending referral code captured via ?ref= before signup.
 * Call after Auth.tsx confirms a fresh user. Safe to call multiple
 * times — the edge function is idempotent.
 */
export async function redeemPendingReferral(): Promise<boolean> {
  const code = consumePendingReferral();
  if (!code) return false;
  try {
    const { data, error } = await supabase.functions.invoke("redeem-referral-code", {
      body: { code },
    });
    if (error) {
      // Don't re-store the code — a failed redemption stays failed.
      console.warn("Referral redemption failed:", error.message);
      return false;
    }
    return data?.code === "REDEEMED";
  } catch (e) {
    console.warn("Referral redemption threw:", e);
    return false;
  }
}
